/**
 * Voice-activity detection for the interviewer inactivity rule.
 *
 * Why the transcript alone is not enough
 * --------------------------------------
 * Beyond Presence only emits transcript text after it has recognised a full
 * utterance. A slow, quiet or very short answer can therefore leave the browser
 * with no student transcript for a long time even though the student is talking,
 * and the inactivity clock used to charge that time to the student and end the
 * interview. The microphone is the honest signal, so it is measured directly.
 *
 * How speech is separated from noise
 * ----------------------------------
 * A frame counts as voice when its energy sits clearly above the *local* noise
 * floor for longer than a syllable. The floor is the 15th percentile of the last
 * few seconds of frames, so steady background noise (fans, traffic, a keyboard,
 * a hum) raises the floor along with it and never reads as speech, while soft
 * speech still stands out above it. A single loud click or door slam never
 * reaches the onset duration, so it cannot reset the clock either.
 *
 * The thresholds are deliberately conservative in the "is present" direction:
 * a false "speaking" only delays a termination, whereas a false "silent" ends a
 * real interview.
 */

/** Never call anything below this level speech, whatever the room is doing. */
export const VOICE_MIN_RMS = 0.006;

/** Speech must exceed the measured noise floor by this factor. */
export const VOICE_NOISE_MULTIPLIER = 2.5;

/** How long voice must be sustained before it counts as an actual response. */
export const VOICE_ONSET_MS = 300;

/** Sampling cadence for the microphone. */
export const VOICE_POLL_MS = 200;

/**
 * After the last voiced frame, the inactivity clock stays paused this long, so
 * the natural pauses inside a sentence are never mistaken for silence.
 */
export const SPEAKING_HOLD_MS = 2500;

/** Frames kept for the noise-floor estimate (~8 s at VOICE_POLL_MS). */
const FLOOR_WINDOW = 40;
/** Below this many frames there is not enough history to estimate a floor. */
const FLOOR_MIN_FRAMES = 8;

/** RMS level of a time-domain buffer (Float32Array from an AnalyserNode). */
export function readVoiceLevel(buffer) {
  if (!buffer || typeof buffer.length !== 'number' || buffer.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < buffer.length; i += 1) {
    const sample = Number(buffer[i]);
    if (Number.isFinite(sample)) sum += sample * sample;
  }
  return Math.sqrt(sum / buffer.length);
}

function percentile(levels, fraction) {
  const sorted = [...levels].sort((a, b) => a - b);
  const index = Math.min(sorted.length - 1, Math.max(0, Math.floor(sorted.length * fraction)));
  return sorted[index];
}

/**
 * Stateful tracker. `feed(rms, now)` returns
 * `{ voiced, level, threshold, floor }` where `voiced` is true only for speech
 * that has been sustained past VOICE_ONSET_MS.
 */
export function createVoiceTracker() {
  const levels = [];
  let onsetAt = null;

  return {
    feed(rms, now = Date.now()) {
      const level = Number.isFinite(rms) && rms > 0 ? rms : 0;
      levels.push(level);
      if (levels.length > FLOOR_WINDOW) levels.shift();

      const floor = levels.length < FLOOR_MIN_FRAMES ? 0 : percentile(levels, 0.15);
      const threshold = Math.max(VOICE_MIN_RMS, floor * VOICE_NOISE_MULTIPLIER);

      if (level > threshold) {
        if (onsetAt === null) onsetAt = now;
        return { voiced: now - onsetAt >= VOICE_ONSET_MS, level, threshold, floor };
      }

      onsetAt = null;
      return { voiced: false, level, threshold, floor };
    },
    reset() {
      levels.length = 0;
      onsetAt = null;
    }
  };
}
